import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Modal,
  Button,
  TextField,
  Stack,
  IconButton,
  Breadcrumbs,
  Link,
  Divider,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import HomeIcon from '@mui/icons-material/Home';
import EventIcon from '@mui/icons-material/Event';
import { db } from '../../firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { format } from 'date-fns';
import dayjs from 'dayjs';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  maxHeight: '90vh',
  overflowY: 'auto',
};

function EventManagement() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventData, setEventData] = useState({
    title: '',
    description: '',
    date: dayjs(),
    location: '',
    organizer: '',
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'events'));
      const eventsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date.toDate(),
      }));
      setEvents(eventsList);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleOpen = (date = new Date()) => {
    setSelectedDate(dayjs(date));
    setEventData({ ...eventData, date: dayjs(date) });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedEvent(null);
    setEventData({
      title: '',
      description: '',
      date: dayjs(),
      location: '',
      organizer: '',
    });
  };

  const handleDateSelect = (selectInfo) => {
    handleOpen(selectInfo.start);
  };

  const handleEventClick = (clickInfo) => {
    const event = events.find(e => e.id === clickInfo.event.id);
    if (event) {
      setSelectedEvent(event);
      setEventData({
        title: event.title,
        description: event.description,
        date: dayjs(event.date),
        location: event.location,
        organizer: event.organizer,
      });
      setOpen(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const eventDataToSave = {
        ...eventData,
        date: Timestamp.fromDate(eventData.date.toDate()),
      };

      if (selectedEvent) {
        await updateDoc(doc(db, 'events', selectedEvent.id), eventDataToSave);
      } else {
        await addDoc(collection(db, 'events'), eventDataToSave);
      }
      handleClose();
      fetchEvents();
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const handleDelete = async () => {
    if (selectedEvent) {
      try {
        await deleteDoc(doc(db, 'events', selectedEvent.id));
        handleClose();
        fetchEvents();
      } catch (error) {
        console.error('Error deleting event:', error);
      }
    }
  };

  return (
    <Box sx={{ 
      flexGrow: 1,
      pl: { xs: 0, sm: '240px' }, // Add padding for sidebar
      pt: { xs: 2, sm: 3 },
      pb: { xs: 2, sm: 3 },
      width: '100%'
    }}>
      <Container maxWidth="lg" sx={{ mt: 0, mb: 4 }}>
        {/* Breadcrumbs */}
       

        <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Event Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpen()}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
                borderRadius: 2,
              }}
            >
              Add New Event
            </Button>
          </Box>
          <Divider sx={{ mb: 3 }} />
          
          <Box sx={{ 
            mt: 3,
            '& .fc': {
              fontFamily: 'inherit',
            },
            '& .fc-toolbar-title': {
              fontSize: '1.5rem',
              fontWeight: 600,
            },
            '& .fc-button': {
              textTransform: 'capitalize',
              fontWeight: 500,
              borderRadius: 1,
            },
            '& .fc-event': {
              cursor: 'pointer',
              borderRadius: 1,
              padding: '2px 4px',
            },
            '& .fc-day-today': {
              backgroundColor: 'rgba(25, 118, 210, 0.05) !important',
            },
            '& .fc-highlight': {
              backgroundColor: 'rgba(25, 118, 210, 0.1) !important',
            },
          }}>
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              selectable={true}
              select={handleDateSelect}
              eventClick={handleEventClick}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,dayGridWeek'
              }}
              events={events.map(event => ({
                id: event.id,
                title: event.title,
                start: event.date,
                backgroundColor: '#1976d2',
                borderColor: '#1976d2',
              }))}
              height="auto"
            />
          </Box>
        </Paper>

        <Modal 
          open={open} 
          onClose={handleClose}
          sx={{
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
            },
          }}
        >
          <Box sx={style}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              {selectedEvent ? 'Edit Event' : 'Add New Event'}
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  label="Event Title"
                  value={eventData.title}
                  onChange={(e) => setEventData({ ...eventData, title: e.target.value })}
                  required
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Description"
                  value={eventData.description}
                  onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
                  multiline
                  rows={4}
                  required
                  fullWidth
                  variant="outlined"
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="Date & Time"
                    value={eventData.date}
                    onChange={(newValue) => setEventData({ ...eventData, date: newValue })}
                    slotProps={{ textField: { fullWidth: true, required: true } }}
                  />
                </LocalizationProvider>
                <TextField
                  label="Location"
                  value={eventData.location}
                  onChange={(e) => setEventData({ ...eventData, location: e.target.value })}
                  required
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Organizer"
                  value={eventData.organizer}
                  onChange={(e) => setEventData({ ...eventData, organizer: e.target.value })}
                  required
                  fullWidth
                  variant="outlined"
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    sx={{ borderRadius: 2, px: 3 }}
                  >
                    Cancel
                  </Button>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {selectedEvent && (
                      <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        startIcon={<DeleteIcon />}
                        sx={{ borderRadius: 2 }}
                      >
                        Delete
                      </Button>
                    )}
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      startIcon={selectedEvent ? <EditIcon /> : <AddIcon />}
                      sx={{ borderRadius: 2 }}
                    >
                      {selectedEvent ? 'Update Event' : 'Add Event'}
                    </Button>
                  </Box>
                </Box>
              </Stack>
            </form>
          </Box>
        </Modal>
      </Container>
    </Box>
  );
}

export default EventManagement;
